import type { ServiceDetail } from "@/types/detail";
import { DEV_WORKFLOW } from "@/data/detail.shared";
import {
  Palette,
  Code2,
  AppWindow,
  Boxes,
  Contact,
  Smartphone,
  Gauge,
  ShieldCheck,
  Zap,
  Users,
  Rocket,
  LineChart,
  Layers,
  Clock,
} from "@/icons";

export const servicesA: ServiceDetail[] = [
  {
    slug: "website-design",
    title: "Website Design & Development",
    category: "Technology Solutions",
    tagline:
      "Modern, responsive websites that strengthen your brand, engage customers, and generate measurable business growth.",
    icon: Palette,
    accent: "#0F766E",
    overview:
      "Your website is often the first impression customers have of your business. We design and develop modern, responsive websites that not only look professional but also support your business goals. Whether you need a corporate website, product showcase, service portal or marketing website, we create fast, secure and SEO-friendly solutions that build credibility, improve user experience and generate revenues.",
    problems: [
      "An outdated website that no longer reflects your business.",
      "Low enquiry or conversion rates despite receiving website traffic.",
      "Slow-loading pages that negatively impact user experience and search engine rankings.",
      "Difficult content management requiring developer assistance."
    ],
    idealCustomers: [
      "Start-ups launching a new business or product.",
      "Small and medium businesses modernising their online presence.",
      "Enterprises requiring a scalable corporate website.",
      "Manufacturing and industrial companies showcasing products and services.",
      "Government and educational organisations requiring accessible public websites."
    ],
    benefits: [
      { title: "Stronger Brand Presence", description: "Build trust with a modern and professional online identity.", icon: LineChart },
      { title: "More qualified leads", description: "Guide visitors towards meaningful enquiries and conversions.", icon: LineChart },
      { title: "Instant credibility", description: "A polished, cohesive brand experience builds trust immediately.", icon: ShieldCheck },
      { title: "Improved User Experience", description: "Fast, intuitive navigation across all devices.", icon: Zap },
      { title: "Future-Ready Platform", description: "Easily expand with new pages, features and integrations as your business grows.", icon: Layers },
    ],
    features: [
      {
        title: "Custom Website Design",
        description: "Professionally designed interfaces tailored to your brand identity, business objectives and target audience."
      },
      {
        title: "Responsive User Experience",
        description: "Optimised layouts that provide a consistent and intuitive experience across desktops, tablets and mobile devices."
      },
      {
        title: "Content Management System",
        description: "Manage pages, images, blogs and other website content without requiring technical expertise."
      },
      {
        title: "SEO-Ready Architecture",
        description: "Built with clean code, semantic structure and technical best practices to improve search engine visibility."
      },
      {
        title: "Lead Generation Forms",
        description: "Enquiry, contact and quotation forms designed to capture and manage potential customer enquiries efficiently."
      },
      {
        title: "Performance & Security",
        description: "Optimised for fast loading, secure communication and protection against common web vulnerabilities."
      },
      {
        title: "Third-Party Integrations",
        description: "Integration with analytics, social media, maps, CRM platforms and other business tools where required."
      },
    ],
    techStack: ["React", "TypeScript", "Tailwind CSS", "Next.js", "Node.js", "REST APIs"],
    workflow: DEV_WORKFLOW,
    faqs: [
      { question: "How long does a website project take?", answer: "Most marketing sites take four to eight weeks depending on page count and content readiness. We'll give you a precise timeline after discovery." },
      { question: "Do you write the copy?", answer: "We can. We offer messaging and copywriting support, or we'll collaborate closely with your team's content." },
      { question: "Will I be able to edit the site myself?", answer: "Yes. We can integrate a user-friendly Content Management System (CMS) that allows authorised users to update content without touching code." },
      { question: "Do you handle hosting?", answer: "Yes. We can deploy your website on reliable cloud hosting, configure your domain, SSL certificate and email services, and provide ongoing hosting and maintenance if required." },
      { question: "Is SEO included?", answer: "Yes. Every website includes technical SEO best practices such as semantic HTML, metadata, XML sitemaps, mobile optimisation and fast page loading." },
      { question: "What do you need from us to start?", answer: "Your goals, brand assets and any existing content. We guide you through the rest in discovery." },
    ],
    relatedServices: ["ui-ux-design", "website-development", "web-applications"],
    relatedIndustries: ["manufacturing", "healthcare", "education", "government", "retail"],
    caseConcept: {
      title: "Corporate Website Transformation",
      category: "Example Project",
      summary:
        "A growing multi-location business needed to modernise its outdated website to better showcase its services, strengthen customer confidence and generate more qualified enquiries. We designed and developed a responsive, SEO-friendly website with a streamlined user experience, clear calls-to-action and an easy-to-manage content management system, providing a scalable digital platform for future growth.",
      metrics: [
        {label: "Lead Enquiries", value: "+34%"},
        {label: "Page Load Time", value: "0.9s"},
        {label: "Website Pages", value: "12"}
      ],
      tech: ["React", "Tailwind CSS", "TypeScript"],
      accent: "#0F766E",
    },
    image: "/images/services/website-development.webp",
  },
  {
    slug: "web-applications",
    title: "Custom Web Application",
    category: "Technology Solutions",
    tagline:
      "Scalable, secure and intelligent web applications designed around your unique business processes, helping you automate operations, improve productivity and support long-term growth.",
    icon: AppWindow,
    accent: "#4F46E5",
    overview:
      "When off-the-shelf software forces your organisation to adapt its processes, a custom web application gives you the freedom to work the way your business actually operates. We design and develop secure, scalable applications that automate workflows, centralise information, integrate with existing systems and provide real-time visibility into business operations. Every solution is built around your current requirements while remaining flexible enough to support future growth.",
    problems: [
      "Disconnected systems and spreadsheets causing duplicate work and inconsistent data.",
      "Manual business processes that reduce productivity and increase operational costs.",
      "Off-the-shelf software that cannot adapt to your unique business requirements.",
      "Limited visibility into business operations due to fragmented information.",
    ],
    idealCustomers: [
      "Businesses replacing spreadsheets and disconnected software with a unified platform.",
      "Organisations requiring software tailored to their unique operational processes.",
      "Growing enterprises seeking to automate workflows and improve operational efficiency.",
      "Startups building innovative Software-as-a-Service (SaaS) platforms.",
      "Government departments and institutions requiring secure business applications.",
    ],
    benefits: [
      {
        title: "Automate Business Processes",
        description: "Reduce manual work, eliminate repetitive tasks and improve operational efficiency through intelligent automation.",
        icon: Layers,
      },
      {
        title: "Centralise Business Information",
        description: "Provide a single, reliable source of information across departments, improving collaboration and decision-making.",
        icon: LineChart,
      },
      {
        title: "Scale with Your Business",
        description: "A flexible architecture that supports increasing users, transactions and business requirements without major redevelopment.",
        icon: Rocket,
      },
      {
        title: "Enterprise-Grade Security",
        description: "Protect sensitive business information through role-based access, encryption, audit trails and secure development practices.",
        icon: ShieldCheck,
      },
    ],
    features: [
      {
        title: "Role-Based Access Control",
        description: "Provide users with secure access to only the information and functions relevant to their responsibilities.",
      },
      {
        title: "Workflow Automation",
        description: "Automate approvals, notifications, assignments and routine business processes to improve efficiency.",
      },
      {
        title: "Real-Time Dashboards",
        description: "Monitor key performance indicators and operational data through configurable dashboards and live reporting.",
      },
      {
        title: "System Integrations",
        description: "Connect with ERP, CRM, payment gateways, third-party APIs and other enterprise systems.",
      },
      {
        title: "Reporting & Analytics",
        description: "Generate operational, analytical and management reports to support informed business decisions.",
      },
      {
        title: "Audit Trail & Activity Logging",
        description: "Maintain a complete history of user activities to support compliance, security and operational transparency.",
      },
    ],
    techStack: ["React", "TypeScript", "FastAPI", "Node.js", "PostgreSQL", "MongoDB", "Redis", "AWS"],
    workflow: DEV_WORKFLOW,
    faqs: [
      { question: "How is this different from off-the-shelf software?", answer: "A custom app fits your exact workflow and data, removing the compromises and workarounds generic tools force on you." },
      { question: "Is a custom build worth the investment?", answer: "When a process is core to your business or generic tools are costing you time and errors, custom typically pays for itself quickly." },
      { question: "Can you integrate with our current systems?", answer: "Yes. We commonly integrate with CRMs, ERPs, payment providers and internal APIs." },
      { question: "How do you handle security?", answer: "Authentication, role-based permissions, encryption and auditing are standard, with compliance tailored to your industry." },
      { question: "Can it scale as we grow?", answer: "We design for scale from the start, so more users and data don't force a rebuild." },
      { question: "Do we own the code?", answer: "Yes. You own the code and IP, with documentation for your team." },
    ],
    relatedServices: ["api-development", "cloud-deployment", "workflow-automation"],
    relatedIndustries: ["manufacturing", "healthcare", "education", "government"],
    caseConcept: {
      title: "Enterprise Operations Platform",
      category: "Example Project",
      summary:
        "A multi-location organisation required a unified web application to replace disconnected spreadsheets and legacy systems used across operations, inventory and reporting. We developed a secure, role-based platform with workflow automation, real-time dashboards and system integrations, enabling faster decision-making, improved collaboration and greater operational efficiency.",
      metrics: [
        { label: "Systems Consolidated", value: "7" },
        { label: "Manual Processing", value: "-60%" },
        { label: "Active Users", value: "300+" },
      ],
      tech: ["React", "FastAPI", "PostgreSQL"],
      accent: "#4F46E5",
    },
    image: "/images/services/web-applications.webp",
  },
  {
    slug: "erp",
    title: "ERP Solutions",
    category: "Technology Solutions",
    tagline:
      "Build a customised ERP platform that unifies business operations, automates workflows and provides real-time visibility across your organisation.",
    icon: Boxes,
    accent: "#0F766E",
    overview:
      "Many organisations invest in ERP software only to discover that it is expensive, complex and requires their teams to adapt to rigid processes. We develop custom ERP solutions designed around the way your organisation actually operates. By integrating departments, automating routine activities and providing real-time business information, our ERP systems improve productivity, operational visibility and decision-making while remaining flexible enough to support future growth.",
    problems: [
      "Departments operating with disconnected systems and inconsistent business data.",
      "Expensive ERP solutions containing unnecessary features and licensing costs.",
      "Manual reconciliation between finance, procurement, inventory and sales.",
      "Limited visibility into business performance due to fragmented information.",
    ],
    idealCustomers: [
      "Manufacturing and production organisations.",
      "Wholesale and distribution businesses.",
      "Multi-location organisations requiring centralised operations.",
      "Healthcare, education and government institutions managing complex business processes.",
      "Growing businesses replacing spreadsheets and disconnected software.",
    ],
    benefits: [
      {
        title: "Unified Business Operations",
        description: "Connect finance, procurement, inventory, sales and other departments through a single integrated platform.",
        icon: Layers,
      },
      {
        title: "Real-Time Business Visibility",
        description: "Access accurate operational data and performance dashboards to support faster and more informed decision-making.",
        icon: LineChart,
      },
      {
        title: "Reduce Operational Costs",
        description: "Automate routine activities, eliminate duplicate work and reduce dependency on multiple software systems.",
        icon: Gauge,
      },
      {
        title: "Improve Accuracy & Compliance",
        description: "Standardised processes, audit trails and controlled access reduce errors while supporting regulatory compliance.",
        icon: ShieldCheck,
      },
    ],
    features: [
      {
        title: "Integrated Business Modules",
        description: "Connect finance, procurement, inventory, sales, HR and other operational functions within a unified platform.",
      },
      {
        title: "Workflow Automation",
        description: "Automate approvals, notifications and repetitive business processes to improve operational efficiency.",
      },
      {
        title: "Role-Based Dashboards",
        description: "Provide personalised dashboards and reports based on each user's responsibilities and access permissions.",
      },
      {
        title: "Reporting & Business Intelligence",
        description: "Generate operational reports, management dashboards and performance analytics using real-time business data.",
      },
      {
        title: "Multi-Location Management",
        description: "Manage multiple branches, warehouses, departments or business units from a single ERP platform.",
      },
      {
        title: "Integration Framework",
        description: "Integrate with existing CRM systems, payment gateways, biometric devices, accounting software and third-party applications.",
      },
    ],
    techStack: ["React", "TypeScript", "FastAPI", "PostgreSQL", "Redis", "Docker", "AWS"],
    workflow: DEV_WORKFLOW,
    faqs: [
      { question: "Why build a custom ERP instead of buying one?", answer: "Custom ERP fits your processes and only includes what you need — avoiding costly licences and painful workarounds." },
      { question: "Can you migrate our existing data?", answer: "Yes. Data migration and validation from your current systems is part of the project." },
      { question: "How long does an ERP project take?", answer: "We deliver in modules so you get value early; a first useful release is often live within a few months." },
      { question: "Will it integrate with our accounting or CRM?", answer: "Yes, integration with finance, CRM and other systems is common." },
      { question: "Is it secure and auditable?", answer: "Role-based access, audit trails and encryption are standard." },
      { question: "Can we add modules later?", answer: "Absolutely — the modular design is built to grow with you." },
    ],
    relatedServices: ["crm-development", "web-applications", "workflow-automation"],
    relatedIndustries: ["manufacturing", "logistics", "healthcare", "education",],
    caseConcept: {
      title: "Multi-Location ERP Implementation",
      category: "Example Project",
      summary:
          "A growing distribution company operating across multiple locations needed to replace disconnected finance, inventory and order management systems with a unified ERP platform. We developed a customised ERP solution that automated procurement, inventory control, order processing and management reporting, providing real-time operational visibility while significantly reducing manual reconciliation and administrative effort.",
      metrics: [
          {label: "Business Locations", value: "5"},
          {label: "Manual Processing", value: "-60%"},
          {label: "Financial Closing Time", value: "-40%"},
      ],
      tech: ["React", "FastAPI", "PostgreSQL"],
      accent: "#0F766E",
    },
    image: "/images/services/erp_solution.webp",
  },
  {
    slug: "crm",
    title: "CRM Solutions",
    category: "Technology Solutions",
    tagline:
      "Build a customised CRM platform that centralises customer information, streamlines sales and service operations, and helps your organisation build lasting customer relationships.",
    icon: Contact,
    accent: "#DB2777",
    overview:
      "Customer relationships are one of your organisation's most valuable assets. We develop customised CRM solutions that centralise customer information, automate sales and service workflows, improve collaboration between departments and provide complete visibility into customer interactions. Whether your focus is lead management, customer support or long-term relationship management, our CRM solutions adapt to your business processes rather than forcing you to change how you work.",
    problems: [
      "Customer information scattered across emails, spreadsheets and multiple systems.",
      "Generic CRM solutions that fail to support your business processes.",
      "Limited visibility into sales pipelines, customer interactions and business performance.",
      "Manual follow-ups and repetitive administrative tasks reducing team productivity.",
    ],
    idealCustomers: [
      "Sales organisations managing complex customer pipelines.",
      "Customer service teams requiring complete interaction history.",
      "Professional service organisations managing long-term client relationships.",
      "Businesses replacing spreadsheets or generic CRM software.",
      "Growing organisations seeking to automate customer engagement processes.",
    ],
    benefits: [
      {
        title: "Strengthen Customer Relationships",
        description: "Maintain a complete view of every customer interaction, helping your teams deliver personalised and consistent service.",
        icon: Users,
      },
      {
        title: "Increase Sales Productivity",
        description: "Automate routine tasks, reminders and follow-ups so your sales team can focus on building relationships and closing opportunities.",
        icon: Zap,
      },
      {
        title: "Improve Business Visibility",
        description: "Monitor sales performance, customer activity and business trends through real-time dashboards and reports.",
        icon: LineChart,
      },
      {
        title: "Make Better Business Decisions",
        description: "Reliable customer data and forecasting enable informed planning and strategic decision-making.",
        icon: Gauge,
      },
    ],
    features: [
      {
        title: "Lead & Opportunity Management",
        description: "Track enquiries, opportunities and sales pipelines from initial contact through successful conversion.",
      },
      {
        title: "Customer Relationship Management",
        description: "Maintain a complete history of customer interactions, communications, documents and activities in one place.",
      },
      {
        title: "Sales Workflow Automation",
        description: "Automate follow-ups, reminders, task assignments and approval workflows to improve operational efficiency.",
      },
      {
        title: "Email & Calendar Integration",
        description: "Synchronise emails, meetings and customer activities with your CRM to reduce manual data entry.",
      },
      {
        title: "Reports & Performance Dashboards",
        description: "Generate real-time sales, service and management reports to support informed business decisions.",
      },
      {
        title: "Role-Based Access Control",
        description: "Ensure users access only the customer information and business functions relevant to their responsibilities.",
      },
    ],
    techStack: ["React", "TypeScript", "FastAPI", "PostgreSQL", "Redis", "AWS"],
    workflow: DEV_WORKFLOW,
    faqs: [
      { question: "How is a custom CRM better than popular tools?", answer: "It fits your exact process, so your team adopts it, and it automates the steps generic CRMs can't." },
      { question: "Can you migrate contacts and history?", answer: "Yes, we use data migration that de-duplicate data from your current CRM or spreadsheets." },
      { question: "Does it integrate with email and calendar?", answer: "Yes — our solution integrates various services which automatically reduces manual logging." },
      { question: "Can we automate our sales workflow?", answer: "Absolutely. Automated tasks, reminders and routing are core features." },
      { question: "Is reporting customisable?", answer: "Custom dashboards and reports are built around the metrics you care about." },
      { question: "Will it scale with our team?", answer: "Yes, it's designed to grow with more users, data and pipelines." },
    ],
    relatedServices: ["erp-development", "web-applications", "ai-integration"],
    relatedIndustries: ["saas", "fintech", "retail", "manufacturing", "healthcare", "education"],
    caseConcept: {
      title: "Customer Relationship Management Platform",
      category: "Example Project",
      summary:
          "A growing B2B organisation needed to replace spreadsheets and disconnected customer records with a unified CRM platform. We developed a customised CRM solution that centralised customer information, automated lead management, streamlined follow-up activities and provided real-time sales dashboards, enabling stronger customer relationships and improved business performance.",
      metrics: [
          {label: "Sales Conversion", value: "+22%"},
          {label: "Administrative Work", value: "-45%"},
          {label: "Forecast Accuracy", value: "+30%"},
      ],
      tech: ["React", "FastAPI", "OpenAI"],
      accent: "#DB2777",
    },
    image: "/images/services/crm-solutions.webp",
  },
  {
    slug: "mobile-apps",
    title: "Mobile App Development",
    category: "Technology Solutions",
    tagline:
      "Design and develop secure, high-performance mobile applications that improve customer engagement, streamline business operations and keep users connected wherever they are.",
    icon: Smartphone,
    accent: "#0891B2",
    overview:
      "Mobile applications have become an essential channel for customer engagement, employee productivity and digital service delivery. We design & develop secure, intuitive mobile applications for Android and iOS that support your business objectives, integrate with existing systems and provide a seamless user experience. Whether you need a customer-facing application, a field workforce solution or an internal business app, we deliver scalable mobile solutions that grow with your organisation.",
    problems: [
      "Customers expect mobile access to your products and services, but no application is available.",
      "Existing mobile applications provide a poor user experience or unreliable performance.",
      "Separate Android and iOS development increases project cost and maintenance effort.",
      "Field employees lack secure mobile access to business information and workflows.",
    ],
    idealCustomers: [
      "Businesses launching customer-facing mobile applications.",
      "Organisations providing digital services through mobile devices.",
      "Companies requiring mobile solutions for field staff and remote teams.",
      "Startups building mobile-first products.",
      "Enterprises extending existing business systems to Android and iOS devices.",
    ],
    benefits: [
      {
        title: "Reach Customers Anywhere",
        description: "Provide customers and employees with secure access to your services anytime and from any location.",
        icon: LineChart,
      },
      {
        title: "Increase User Engagement",
        description: "Push notifications, offline capabilities and intuitive user experiences encourage regular interaction.",
        icon: Zap,
      },
      {
        title: "Reduce Development Costs",
        description: "Cross-platform development enables Android and iOS applications to be delivered efficiently from a shared codebase.",
        icon: Layers,
      },
      {
        title: "Reliable & Secure Mobile Platform",
        description: "Modern development practices ensure your application remains secure, stable and ready for future growth.",
        icon: ShieldCheck,
      },
    ],
    features: [
      {
        title: "Cross-Platform Mobile Applications",
        description: "Develop Android and iOS applications from a shared codebase while maintaining a consistent user experience.",
      },
      {
        title: "Offline Data Synchronisation",
        description: "Allow users to continue working without an internet connection and automatically synchronise data when connectivity returns.",
      },
      {
        title: "Secure Authentication",
        description: "Support biometric authentication, multi-factor authentication and secure user login.",
      },
      {
        title: "Push Notifications",
        description: "Deliver personalised notifications, reminders and important updates directly to users' devices.",
      },
      {
        title: "Device Integration",
        description: "Access cameras, GPS, barcode scanners, QR codes and other mobile device capabilities where required.",
      },
      {
        title: "Analytics & Monitoring",
        description: "Monitor application usage, crashes and performance to continuously improve the user experience.",
      },
    ],
    techStack: ["React Native", "TypeScript", "Android", "iOS", "Firebase", "REST APIs"],
    workflow: DEV_WORKFLOW,
    faqs: [
      { question: "Do you build native or cross-platform?", answer: "Usually cross-platform for cost and speed, going fully native where the product truly needs it. We'll advise on the right choice." },
      { question: "Can you launch to the app stores for us?", answer: "Yes — we manage submission, review and release on both stores." },
      { question: "Will it work offline?", answer: "We can build offline-first features so the app stays useful without a connection." },
      { question: "Can you reuse our existing backend?", answer: "Yes, we integrate with your existing APIs and data where possible." },
      { question: "Do you support the app after launch?", answer: "We offer ongoing maintenance, updates and improvements." },
      { question: "How long to a first release?", answer: "A focused first version often reaches the stores within a few months." },
    ],
    relatedServices: ["ui-ux-design", "api-development", "maintenance-support"],
    relatedIndustries: ["healthcare", "manufacturing", "logistics", "education"],
    caseConcept: {
        title: "Customer & Field Service Mobile Application",
        category: "Example Project",
        summary:
            "A multi-location service organisation required a mobile application for customers and field engineers to improve service delivery and communication. We developed a cross-platform mobile application with secure authentication, appointment scheduling, real-time notifications, offline data synchronisation and integration with the organisation's existing business systems, enabling faster service delivery and improved customer satisfaction.",
        metrics: [
            {label: "User Adoption", value: "+42%"},
            {label: "Customer Rating", value: "4.8"},
            {label: "Supported Platforms", value: "Android & iOS"},
        ],
        tech: ["React Native", "Node.js", "Firebase"],
        accent: "#0891B2",
    },
    image: "/images/services/mobile-app.webp",
  },
];
